import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  Typography,
  Button,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import {
  CloseOutlined,
  EditOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import CorporateApi from "Services/Corporate";

//Data
import { getCountryCodes } from "Utils/country";

const CorporateUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState([]);
  const [countryCodeData, setCountryCodeData] = useState([]);

  const maxFileSize = 5242880;

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert("File size is larger than 5MB");
      return { code: "file-too-large", message: "File too large" };
    }
    return null;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImagefile", file);
          formik.setFieldValue("passengerProfileImage", base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  useEffect(() => {
    try {
      const countries = getCountryCodes();
      setCountryCodeData(countries);
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  const getDetails = useCallback(() => {
    if (!id) return;
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    CorporateApi.getCorporateDetails(id).then(onSuccess, onFailure);
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog) {
      getDetails();
    }
  }, [openDialog, getDetails]);

  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("firstName", details?.firstName || "");
      formik.setFieldValue("displayName", details?.displayName || "");
      formik.setFieldValue("mobile", details?.mobileNumber || "");
      formik.setFieldValue("email", details?.email || "");
      formik.setFieldValue("secondaryEmail", details?.secondaryEmail || "");
      formik.setFieldValue("panNumber", details?.panNumber || "");
      formik.setFieldValue("address", details?.address || "");
      formik.setFieldValue("country", details?.country || "");
      formik.setFieldValue("smsMailenable", details?.smsMailenable ?? false);
      formik.setFieldValue("passengerProfileImagefile", details?.image || "");
      formik.setFieldValue("passengerProfileImage", details?.image || "");
    }
  }, [details]);

  const handleUpdateCorporate = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      firstName: formValues?.firstName,
      displayName: formValues?.displayName,
      mobileNumber: formValues?.mobile,
      email: formValues?.email,
      secondaryEmail: formValues?.secondaryEmail,
      panNumber: formValues?.panNumber,
      address: formValues?.address,
      country: formValues?.country,
      smsMailenable: formValues?.smsMailenable,
      image: formValues?.passengerProfileImagefile,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "Corporate Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );
        formik.resetForm();
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating corporate",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    CorporateApi.updateCorporate(id, { ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      displayName: "",
      email: "",
      secondaryEmail: "",
      panNumber: "",
      country: "",
      mobile: "",
      address: "",
      smsMailenable: false,
      passengerProfileImage: "",
      passengerProfileImagefile: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("Corporate Name is required").nullable(),
      displayName: yup.string().required("Display Name is required").nullable(),
      email: yup.string().email("Invalid email format").required("Email is required").nullable(),
      secondaryEmail: yup.string().email("Invalid email format").required("Secondary Email is required").nullable(),
      mobile: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      panNumber: yup.string().required("PAN Number is required").nullable(),
      country: yup.string().required("Country is required").nullable(),
      address: yup.string().required("Address is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdateCorporate,
  });

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      {/* Header */}
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>Update Corporate</Typography>
        <IconButton className={classes.closeIconBtn} onClick={handleClose}>
          <CloseOutlined />
        </IconButton>
      </Box>

      {/* Body */}
      <Box className={classes.dialogBody}>
        {/* Left – Profile Photo */}
        <Box className={classes.leftColumn}>
          <Box className={classes.avatarWrapper}>
            <img
              src={formik.values.passengerProfileImage || "/assets/images/user/avatar.png"}
              alt="Profile"
              className={classes.avatarImg}
            />
            <Box
              component="span"
              className={classes.editIconOverlay}
              {...getRootProps()}
              onClick={open}
            >
              <input {...getInputProps()} />
              <EditOutlined />
            </Box>
          </Box>
          <Typography className={classes.photoLabel}>Corporate Logo</Typography>
          <Typography className={classes.photoSubLabel}>JPG, PNG up to 5MB</Typography>
        </Box>

        {/* Right – Form */}
        <Box className={classes.rightColumn}>
          <Grid container spacing={2}>
            {/* First Name + Display Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Legal Name</Typography>
              <TextField
                id="firstName"
                name="firstName"
                placeholder="e.g. Acme Corp"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Display Name</Typography>
              <TextField
                id="displayName"
                name="displayName"
                placeholder="e.g. Acme Inc"
                value={formik.values.displayName}
                onChange={formik.handleChange}
                error={formik.touched.displayName && Boolean(formik.errors.displayName)}
                helperText={formik.touched.displayName && formik.errors.displayName}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Primary Email + Secondary Email */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Primary Email</Typography>
              <TextField
                id="email"
                name="email"
                placeholder="corporate@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Secondary Email</Typography>
              <TextField
                id="secondaryEmail"
                name="secondaryEmail"
                placeholder="info@example.com"
                value={formik.values.secondaryEmail}
                onChange={formik.handleChange}
                error={formik.touched.secondaryEmail && Boolean(formik.errors.secondaryEmail)}
                helperText={formik.touched.secondaryEmail && formik.errors.secondaryEmail}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Mobile & PAN Number */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <TextField
                name="mobile"
                id="mobile"
                placeholder="000 000 0000"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>PAN / Tax ID Number</Typography>
              <TextField
                name="panNumber"
                id="panNumber"
                placeholder="Enter PAN Number"
                value={formik.values.panNumber}
                onChange={formik.handleChange}
                error={formik.touched.panNumber && Boolean(formik.errors.panNumber)}
                helperText={formik.touched.panNumber && formik.errors.panNumber}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>

            {/* Country & SMS/Mail enable */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                name="country"
                id="country"
                select
                value={formik.values.country || ""}
                onChange={formik.handleChange}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => v || <span style={{ color: "#9ca3af" }}>Select country</span>,
                }}
              >
                {countryCodeData.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {`${country.code} (${country.name})`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>SMS / Email Notifications</Typography>
              <TextField
                name="smsMailenable"
                id="smsMailenable"
                select
                value={formik.values.smsMailenable}
                onChange={formik.handleChange}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              >
                <MenuItem value={true}>Enabled</MenuItem>
                <MenuItem value={false}>Disabled</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.formLabel}>Address</Typography>
              <TextField
                name="address"
                id="address"
                placeholder="Enter address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.dialogFooter}>
        <Button
          className={classes.cancelButton}
          onClick={() => {
            formik.resetForm();
            handleClose();
          }}
        >
          Close
        </Button>
        <Button
          className={classes.submitButton}
          onClick={formik.handleSubmit}
          startIcon={<SaveOutlined />}
        >
          Update Corporate
        </Button>
      </Box>
    </Dialog>
  );
};

export default CorporateUpdate;
